// ── WrapCelebration ───────────────────────────────────────────────────────────
// The wrap moment: a season finale, the series finale, or catching up on a season.
// v17 facelift (2026-09-15, Jason: "keep the same concepts but they need facelifts",
// designed from the Claude Design show-page mockup's Finale state): the show's own
// tint bleeds down over the page for 600ms, one serif line rises after a beat, and
// the reaction row / notify button / race beats follow 80ms apart. No clapboard,
// no confetti, no shake. Tap anywhere to leave; everything reverses in 500ms.
//
// The CONTRACT is unchanged: listens for `nextep:wrap` (detail.type = 'season' |
// 'series' | 'caughtup', plus race fields), raises __nextepWrapUntil /
// __nextepFinaleActive while up, and fires `nextep:wrap-done` on dismiss so the
// held Cinephile card can reveal. Reactions still post a 'reacted' activity.
// Mirrors the AddShowSpotlight pattern — position:fixed inside the React tree.

import { logEvent } from '../lib/analytics'
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { writeActivity } from '../lib/activity'
import { requestPermission, subscribeToPush } from '../lib/push'
import { paletteFromPoster } from '../lib/nowPlaying'
import { Clapboard } from './ClapboardIcon'
import { useShareCard } from '../hooks/useShareCard'
import { observe } from '../lib/batchReceipt'

const REACTIONS = ['😍', '🔥', '😭', '🤯', '😂', '😬']
const EASE_OUT = 'cubic-bezier(0.22,1,0.36,1)'

export function WrapCelebration() {
  const navigate    = useNavigate()
  const { user, profile } = useAuth()
  const [shareLabel, shareCard, sharing] = useShareCard()
  const userRef    = useRef(user)
  const profileRef = useRef(profile)
  useEffect(() => { userRef.current = user; profileRef.current = profile }, [user, profile])
  const [detail, setDetail]   = useState(null)
  const [visible, setVisible] = useState(false)
  const [flood, setFlood]     = useState(false)   // tint has covered the page
  const [line, setLine]       = useState(false)   // serif line + beats are in
  const [exiting, setExiting] = useState(false)
  // The clapboard closes the moment: it rises open with the line, then slams a beat later.
  const [slam, setSlam] = useState(false)
  useEffect(() => { if (!line) { setSlam(false); return } const t = setTimeout(() => setSlam(true), 420); return () => clearTimeout(t) }, [line])
  const [reacted, setReacted] = useState(null)
  const [notify,  setNotify]  = useState(null)    // caught-up: null | 'pending' | 'on'
  const [copied,  setCopied]  = useState(false)
  const [pal, setPal]         = useState(null)
  const timers = useRef([])

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = [] }
  const later = (fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t) }

  useEffect(() => {
    const handler = (e) => {
      clearTimers()
      observe('wrap', e.detail)   // listed on a cascade's receipt; the cinematic still plays
      logEvent('cinematic_shown', { kind: 'wrap', type: e.detail?.type, show_id: e.detail?.showId })
      setDetail(e.detail)
      setFlood(false); setLine(false); setExiting(false); setReacted(null); setNotify(null); setPal(null)
      setVisible(true)
      window.__nextepWrapUntil = Date.now() + 6000
      window.__nextepFinaleActive = true
      if (e.detail?.poster) paletteFromPoster(e.detail.poster).then(p => setPal(p)).catch(() => {})
      // The band's tint bleeds down over 600ms; the line rises after a 200ms beat.
      requestAnimationFrame(() => setFlood(true))
      later(() => setLine(true), 800)
      if (!userRef.current) later(() => dismiss(), e.detail?.type === 'season' ? 5200 : 4200)
    }
    window.addEventListener('nextep:wrap', handler)
    return () => { window.removeEventListener('nextep:wrap', handler); clearTimers() }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const react = (emoji) => {
    if (reacted) return
    setReacted(emoji)
    const u = userRef.current
    if (u && detail?.showId) {
      const extra = { emoji }
      if (detail.type === 'season' && detail.season) extra.season = detail.season
      writeActivity(u.id, profileRef.current?.share_activity !== false, 'reacted',
        { id: detail.showId, name: detail.showName, poster_path: detail.poster }, extra)
    }
    later(() => dismiss(), 1300)
  }

  const notifyMe = async () => {
    if (notify) return
    setNotify('pending')
    try {
      const perm = await requestPermission()
      const u = userRef.current
      if (perm === 'granted' && u?.id) await subscribeToPush(u.id)
    } catch { /* the cron covers watchlisted shows either way */ }
    setNotify('on')
  }

  const dismiss = (e, opts = {}) => {
    e?.stopPropagation()
    clearTimers()
    window.__nextepWrapUntil = 0
    window.__nextepFinaleActive = false
    if (!opts?.silent) window.dispatchEvent(new CustomEvent('nextep:wrap-done'))
    setExiting(true)
    later(() => { setVisible(false); setDetail(null); setLine(false); setFlood(false) }, 500)
  }

  if (!visible || !detail) return null

  const isSeries   = detail.type === 'series'
  const isCaughtUp = detail.type === 'caughtup'
  const inRace     = (isSeries || isCaughtUp) && !!detail.raceOpponent
  const raceWon    = inRace && detail.raceWon === true
  const isCoFinale = inRace && !detail.raceWon
  const eyebrow = isSeries ? 'Series finale' : isCaughtUp ? `Season ${detail.season || ''} · caught up` : `Season ${detail.season} · finale`
  const headline = isSeries ? 'That’s a wrap.' : isCaughtUp ? 'All caught up.' : `Season ${detail.season}, done.`
  const sub = raceWon ? `You beat ${detail.raceOpponent} to the finale`
    : isCoFinale ? `You and ${detail.raceOpponent} crossed the line together`
    : isCaughtUp ? `${detail.showName} · more episodes coming`
    : detail.showName
  const top = pal?.raised || 'var(--bg-raised)'
  const mid = pal?.bg || 'var(--bg)'
  // Class-driven (2026-10-11 cinematics audit): an inline `animation` restarts whenever
  // the element re-renders, and the async palette fetch re-renders this tree mid-beat,
  // so the lines sat at opacity 0 for a second or more on a phone. The delay rides a
  // custom property instead.
  const beat = (i) => ({ '--beat': `${i * 80}ms` })

  return (
    <>
      <style>{`
        @keyframes nxWrapUp { from { opacity: 0; transform: translateY(12px) } to { opacity: 1; transform: none } }
        .nx-wrap .nx-beat { animation: nxWrapUp 400ms ${EASE_OUT} both; animation-delay: var(--beat, 0ms); }
        @keyframes nxWrapPop { 0% { transform: scale(0.6); opacity: 0 } 60% { transform: scale(1.12); opacity: 1 } 100% { transform: scale(1); opacity: 1 } }
        @media (prefers-reduced-motion: reduce) { .nx-wrap * { animation-duration: 1ms !important; transition-duration: 1ms !important } }
      `}</style>
      <div className="nx-wrap" onClick={dismiss} style={{
        position: 'fixed', inset: 0, zIndex: 'var(--z-celebration)', fontFamily: 'var(--font)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 20px',
        opacity: exiting ? 0 : 1, transition: 'opacity 500ms ease',
      }}>
        {/* The tint flood: starts as the band at the top and bleeds down to cover the page */}
        <div aria-hidden="true" style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(180deg, ${top} 0%, ${mid} 60%, var(--bg) 100%)`,
          opacity: flood ? 0.94 : 0,
          transform: flood ? 'scaleY(1)' : 'scaleY(0.28)', transformOrigin: '50% 0%',
          transition: 'transform 600ms cubic-bezier(0.45,0,0.55,1), opacity 600ms cubic-bezier(0.45,0,0.55,1)',
        }} />

        {line && (
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28, maxWidth: 420, width: '100%', textAlign: 'center' }}>
            <div className="nx-beat" style={{ ...beat(0), display: 'flex', justifyContent: 'center', marginBottom: -8 }}><Clapboard size={64} watched={slam} accent="var(--amber)" idle="rgba(var(--ink-rgb),0.7)" /></div>
            <div className="nx-beat" style={{ ...beat(0), fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)' }}>{eyebrow}</div>
            <div className="nx-beat" style={{ ...beat(1), fontFamily: 'var(--font-serif)', fontWeight: 400, fontStyle: 'italic', fontSize: 40, lineHeight: 1.1, color: '#fff', letterSpacing: '-0.01em', marginTop: -12 }}>{headline}</div>
            <div className="nx-beat" style={{ ...beat(2), fontSize: 14, color: 'var(--text-muted)', marginTop: -14 }}>{sub}</div>

            {/* The stamp row, exactly as it will sit on the Final Cut card when the show
                is finished: one bar per season in the show's own colour, the ones you've
                closed lit, the rest outlined. The collecting feeling lives HERE, at the
                season finale — the card itself still only arrives whole, at the end
                (Jason 2026-09-20: stamps on finished cards, a preview in the wrap, no
                in-progress cards). Ten bars then the count, same rule as the card. */}
            {!isSeries && !isCaughtUp && detail.totalSeasons > 1 && (() => {
              const total = detail.totalSeasons, done = Math.min(detail.season, total), toGo = total - done
              const vivid = pal?.vivid || pal?.accent || 'var(--amber)'
              const shown = Math.min(total, 10)
              return (
                <div className="nx-beat" style={{ ...beat(3), display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    {Array.from({ length: shown }, (_, i) => i + 1).map(n => {
                      const lit = n <= done, just = n === done
                      return <span key={n} style={{ width: 18, height: 5, borderRadius: 3, background: lit ? `color-mix(in srgb, ${vivid} 85%, #fff)` : 'transparent', border: lit ? 'none' : '1px solid rgba(var(--ink-rgb),0.22)', boxSizing: 'border-box', boxShadow: lit ? `0 0 8px color-mix(in srgb, ${vivid} 55%, transparent)` : 'none', '--nxa': just ? 'nxWrapPop 420ms cubic-bezier(0.34,1.2,0.64,1) both' : 'none', animationDelay: just ? '360ms' : '0ms' }} />
                    })}
                    {total > 10 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'rgba(var(--ink-rgb),0.7)', marginLeft: 3 }}>×{total}</span>}
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>
                    Season {done} stamped{toGo > 0 ? ` · ${toGo} to go` : ' · every season'}
                  </div>
                </div>
              )
            })()}

            {/* Race win: the shareable beat */}
            {raceWon && (
              <div className="nx-beat" style={{ ...beat(3), display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button onClick={async e => {
                  e.stopPropagation()
                  const url = `https://app.nextep.show/show/${detail.showId}?race=${encodeURIComponent(detail.raceOpponent)}`
                  const text = `I beat ${detail.raceOpponent} to the finale of ${detail.showName} on NextEp 🏁`
                  try {
                    if (navigator.share) await navigator.share({ title: 'NextEp', text, url })
                    else { await navigator.clipboard.writeText(`${text} ${url}`); setCopied(true); setTimeout(() => setCopied(false), 1800) }
                  } catch (_) {}
                }} style={{ padding: '11px 18px', background: 'var(--surface)', color: 'var(--amber)', border: '1px solid rgba(var(--amber-rgb),0.35)', borderRadius: 'var(--radius-pill)', fontWeight: 600, fontSize: 14, fontFamily: 'var(--font)', cursor: 'pointer' }}>
                  {copied ? 'Copied' : 'Share the win'}
                </button>
                <button onClick={e => { dismiss(e, { silent: true }); navigate('/people') }} style={{ padding: '11px 20px', background: 'var(--amber)', color: 'var(--amber-ink)', border: 'none', borderRadius: 'var(--radius-pill)', fontWeight: 700, fontSize: 14, fontFamily: 'var(--font)', cursor: 'pointer' }}>
                  Race someone new
                </button>
              </div>
            )}

            {/* Caught up: the wait begins → notify opt-in */}
            {user && isCaughtUp && (
              <div className="nx-beat" style={beat(3)}>
                {notify === 'on'
                  ? <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>You’re on the list. We’ll ping you when it’s back.</div>
                  : <button onClick={e => { e.stopPropagation(); notifyMe() }} disabled={notify === 'pending'} style={{ padding: '11px 20px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-pill)', color: 'var(--text-strong)', fontWeight: 600, fontSize: 14, fontFamily: 'var(--font)', cursor: notify === 'pending' ? 'default' : 'pointer', opacity: notify === 'pending' ? 0.6 : 1 }}>
                      {notify === 'pending' ? 'Setting up…' : 'Tell me when it’s back'}
                    </button>}
              </div>
            )}

            <div className="nx-beat" style={beat(4)}>
              <button disabled={sharing} aria-busy={sharing || undefined} onClick={e => { e.stopPropagation(); shareCard({ show: { name: detail.showName, poster_path: detail.poster }, eyebrow: eyebrow, headline: headline, line: detail.showName }) }} style={{ height: 40, padding: '0 18px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 13.5, fontWeight: 600, cursor: sharing ? 'default' : 'pointer', opacity: sharing ? 0.7 : 1 }}>{shareLabel}</button>
            </div>
            {/* How'd it hit you: the one-tap reaction, shared to the feed */}
            {user && !isCaughtUp && (
              <div className="nx-beat" style={beat(4)}>
                {reacted ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: 'var(--text-muted)', fontSize: 14 }}>
                    <span style={{ fontSize: 28, display: 'inline-block', '--nxa': 'nxWrapPop 420ms cubic-bezier(0.34,1.2,0.64,1) both' }}>{reacted}</span> shared with your friends
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 12 }}>How’d it hit you?</div>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                      {REACTIONS.map((em, i) => (
                        <button key={em} onClick={e => { e.stopPropagation(); react(em) }} style={{ width: 44, height: 44, borderRadius: '50%', fontSize: 22, lineHeight: 1, background: 'var(--bg-card)', border: '1px solid var(--border)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', '--nxa': `nxWrapUp 320ms ${EASE_OUT} both`, animationDelay: `${320 + i * 80}ms` }}>{em}</button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {!reacted && (
              <div className="nx-beat" style={{ ...beat(5), display: 'flex', gap: 14, alignItems: 'center' }}>
                <button onClick={dismiss} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: 13, fontFamily: 'var(--font)', cursor: 'pointer', padding: '8px 4px' }}>Tap anywhere to continue</button>
                {isSeries && <button onClick={e => { dismiss(e, { silent: true }); navigate('/list') }} style={{ background: 'none', border: 'none', color: 'var(--amber)', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font)', cursor: 'pointer', padding: '8px 4px' }}>Open My List</button>}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
